import { ChartArtifact_ } from "@redotech/ai-core-agent-artifact-schema/artifact-schema";
import * as z from "zod/v4";

const DisplayMessageItem_ = z.object({
  type: z.literal("message"),
  role: z.enum(["user", "assistant"]),
  content: z.string(),
  fileNames: z.array(z.string()).optional(),
});

const DisplayToolCallStatusItem_ = z.object({
  type: z.literal("tool-call-status"),
  toolCallId: z.string(),
  toolName: z.string(),
  input: z.unknown(),
  isComplete: z.literal(true),
});

const DisplayAskUserQuestionItem_ = z.object({
  type: z.literal("ask-user-question"),
  toolCallId: z.string(),
  input: z.object({
    questionType: z.enum(["singleSelect", "multiSelect"]),
    question: z.string(),
    questionContext: z.string().nullable().optional(),
    answerOptions: z.array(
      z.object({
        optionTitle: z.string(),
        optionDescription: z.string().nullable().optional(),
      }),
    ),
  }),
  responded: z.boolean().optional(),
  response: z
    .object({
      selectedOptions: z.array(z.string()).optional(),
      otherText: z.string().optional(),
    })
    .optional(),
});

const DisplayUserConfirmationItem_ = z.object({
  type: z.literal("user-confirmation"),
  toolCallId: z.string(),
  rejected: z.boolean(),
});

const DisplayCompletedOperationItem_ = z.object({
  type: z.literal("completed-operation"),
  operationType: z.enum([
    "create",
    "update",
    "delete",
    "link_suppliers",
    "create_supplier",
    "update_supplier",
    "create_bom",
    "update_bom",
    "create_location",
    "set_product_locations",
    "create_purchase_order",
    "update_purchase_order",
    "adjust_inventory",
    "create_transfer",
    "submit_transfer",
  ]),
  productCount: z.number().optional(),
  supplierCount: z.number().optional(),
  bomCount: z.number().optional(),
  locationCount: z.number().optional(),
  purchaseOrderCount: z.number().optional(),
  transferCount: z.number().optional(),
  linksUpdated: z.number().optional(),
  fields: z.array(z.string()).optional(),
  forecastingEnabled: z.boolean().optional(),
  toolCallId: z.string().optional(),
  isUndone: z.boolean().optional(),
  adjustmentSessionKind: z.string().optional(),
  failed: z.boolean().optional(),
});

const DisplayFileExportItem_ = z.object({
  type: z.literal("file-export"),
  fileName: z.string(),
  s3Key: z.string(),
  toolCallId: z.string().optional(),
});

const DisplayTableItem_ = z.object({
  type: z.literal("table"),
  title: z.string().optional(),
  headers: z.array(z.string()),
  rows: z.array(z.array(z.string())),
  toolCallId: z.string().optional(),
});

const DisplayChartItem_ = z.object({
  type: z.literal("chart"),
  chart: ChartArtifact_,
  toolCallId: z.string().optional(),
});

export const DisplayItemSchema = z.discriminatedUnion("type", [
  DisplayMessageItem_,
  DisplayToolCallStatusItem_,
  DisplayCompletedOperationItem_,
  DisplayAskUserQuestionItem_,
  DisplayUserConfirmationItem_,
  DisplayFileExportItem_,
  DisplayTableItem_,
  DisplayChartItem_,
]);
export type DisplayItem = z.infer<typeof DisplayItemSchema>;

export const getIntelligentInventoryThreadMessages = {
  name: "getIntelligentInventoryThreadMessages" as const,
  input: z.object({ threadId: z.string() }),
  output: z.object({
    threadId: z.string(),
    items: z.array(DisplayItemSchema),
    pendingConfirmations: z.array(
      z.object({
        toolCallId: z.string(),
        toolName: z.string(),
        confirmationData: z.unknown(),
        confirmationPrompt: z.string(),
      }),
    ),
  }),
};
